import React, { Component, Fragment } from 'react';
import { Draggable } from 'react-beautiful-dnd';

import classNames from 'classnames';
import { ButtonIcon } from 'components/button';
import Collapse from 'components/collapse';
import { Loading } from 'components/cosmos';
import { translate } from 'components/translate';
import { inject } from 'mobx-react';
import {
  EVENT_ACTIONS,
  EVENTS_INTERACTIONS,
  EVENTS_CATEGORY,
  EVENTS_LABELS
} from 'modules/app/services/datahub/constants';
import DatahubService from 'modules/app/services/datahub/DatahubService';
import { buildInteractionEventData } from 'modules/app/services/datahub/helper';
import { ContentService } from 'modules/content';
import { ITEM_TYPE } from 'modules/content/constants';
import PropTypes from 'prop-types';

import DroppableContent from './DroppableContent';

import { DragHandler } from '../../../../components/dragHandler';
import ContentEvents from '../../events';

import './DraggableContent.scss';

@translate()
@inject((stores) => ({
  allModulesLoadedPages: stores.ContentStore.allModulesLoadedPages,
  allModulesExtraLoadedPages: stores.ContentStore.allModulesExtraLoadedPages,
  isReorderingModulesEnabled: stores.ContentStore.isReorderingModulesEnabled
}))
export default class DraggableContent extends Component {
  static propTypes = {
    type: PropTypes.string,
    id: PropTypes.string,
    index: PropTypes.number,
    className: PropTypes.string,
    item: PropTypes.object,
    isMobile: PropTypes.bool,
    isExtra: PropTypes.bool,
    isModuleDragging: PropTypes.bool,
    isPageDragging: PropTypes.bool,
    isDraggingOver: PropTypes.bool,
    checkboxChange: PropTypes.func,
    dragOnMouseUp: PropTypes.func,
    dragOnMouseDown: PropTypes.func,
    expandModules: PropTypes.bool,
    checkAllpages: PropTypes.func,
    onClick: PropTypes.func,
    checkedContent: PropTypes.object,
    children: PropTypes.node,
    checkAll: PropTypes.bool,
    allModulesLoadedPages: PropTypes.bool,
    allModulesExtraLoadedPages: PropTypes.bool,
    isReorderingModulesEnabled: PropTypes.bool,
    onDragClick: PropTypes.func,
    updateModulesOpen: PropTypes.func,
    openModulesList: PropTypes.array
  };

  constructor(props) {
    super(props);

    this.divElementRef = React.createRef();

    this.state = {
      isModuleDragging: props.isModuleDragging,
      isPageDragging: props.isPageDragging,
      isDraggingOver: props.isDraggingOver,
      expandModules: props.expandModules,
      checkedContent: props.checkedContent,
      showToggle: true,
      openModule:
        props.expandModules ||
        props.id === ContentService.lastEditedModule ||
        (props.openModulesList || []).includes(props.id),
      nestedChildren: []
    };
  }

  async componentDidMount() {
    const { type, item } = this.props;

    if (type === ITEM_TYPE.MODULE && item.loadedPages) {
      const nestedChildren = item.pages;

      this.setState({ nestedChildren });
    }

    const scrollToLastModule = () => {
      const { lastEditedModule, lastCreatedModule } = ContentService;
      const lastModuleId = lastEditedModule || lastCreatedModule;

      if (lastModuleId && lastModuleId === item.id) {
        this.divElementRef.current?.scrollIntoView({
          block: 'start',
          behavior: 'smooth'
        });
      }
    };

    const timeoutToAvoidFreezeOnRouteChange = 100;

    this.loadingTimer = setTimeout(
      scrollToLastModule,
      timeoutToAvoidFreezeOnRouteChange
    );
  }

  componentWillUnmount() {
    clearTimeout(this.loadingTimer);
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (prevState && nextProps) {
      return DraggableContent.getDraggableContent(prevState, nextProps);
    }

    return null;
  }

  static checkAndUpdateExpandModules(nextProps, prevState) {
    if (
      JSON.stringify(nextProps.expandModules) !==
      JSON.stringify(prevState.expandModules)
    ) {
      if (
        nextProps.expandModules &&
        !nextProps.item.loadedPages &&
        nextProps.item.totalPages
      ) {
        ContentService.fetchModulePagesList(
          nextProps.id,
          nextProps.isExtra ? nextProps.isExtra : false
        );
      }

      return {
        openModule: DraggableContent.getOpenModule(nextProps),
        expandModules: nextProps.expandModules,
        isModuleDragging: nextProps.isModuleDragging
      };
    }
  }

  static checkAndUpdateModuleDragging(nextProps, prevState) {
    if (
      JSON.stringify(nextProps.isModuleDragging) !==
      JSON.stringify(prevState.isModuleDragging)
    ) {
      return { isModuleDragging: nextProps.isModuleDragging };
    }
  }

  static checkAndUpdatePageDragging(nextProps, prevState) {
    if (
      JSON.stringify(nextProps.isPageDragging) !==
      JSON.stringify(prevState.isPageDragging)
    ) {
      return { isPageDragging: nextProps.isPageDragging };
    }
  }

  static checkAndUpdateDraggingOver(nextProps, prevState) {
    if (
      JSON.stringify(nextProps.isDraggingOver) !==
      JSON.stringify(prevState.isDraggingOver)
    ) {
      return { isDraggingOver: nextProps.isDraggingOver };
    }
  }

  static checkAndUpdateCheckedContent(nextProps, prevState) {
    if (
      JSON.stringify(nextProps.checkedContent) !==
      JSON.stringify(prevState.checkedContent)
    ) {
      return { checkedContent: nextProps.checkedContent };
    }
  }

  static checkAndUpdateLoadedPages(nextProps, prevState) {
    if (
      JSON.stringify(nextProps.item.loadedPages) &&
      JSON.stringify(nextProps.item.pages) !==
        JSON.stringify(prevState.nestedChildren)
    ) {
      return { nestedChildren: nextProps.item.pages };
    }
  }

  static getOpenModule(nextProps) {
    return (
      !nextProps.isModuleDragging &&
      (nextProps.expandModules ||
        nextProps.id === ContentService.lastEditedModule)
    );
  }

  static getDraggableContent(prevState, nextProps) {
    return (
      DraggableContent.checkAndUpdateExpandModules(nextProps, prevState) ||
      DraggableContent.checkAndUpdateModuleDragging(nextProps, prevState) ||
      DraggableContent.checkAndUpdatePageDragging(nextProps, prevState) ||
      DraggableContent.checkAndUpdateDraggingOver(nextProps, prevState) ||
      DraggableContent.checkAndUpdateCheckedContent(nextProps, prevState) ||
      DraggableContent.checkAndUpdateLoadedPages(nextProps, prevState) ||
      null
    );
  }

  handleMouseEnter = () => {
    const { isPageDragging } = this.state;

    if (!isPageDragging) {
      this.setState({ showToggle: true });
    }
  };

  handleMouseLeave = () => {
    const { isPageDragging } = this.state;

    if (!isPageDragging) {
      this.setState({ showToggle: false });
    }
  };

  onToggle = async () => {
    const {
      id,
      item,
      isExtra,
      checkAllpages,
      checkedContent,
      checkAll,
      updateModulesOpen
    } = this.props;
    const label = !this.state.openModule
      ? EVENTS_LABELS.CONTENT.MANAGE_MODULE.EXPAND_PAGES
      : EVENTS_LABELS.CONTENT.MANAGE_MODULE.RETRACT_PAGES;

    ContentEvents.sendModuleEvent({ label });

    DatahubService.sendEvent({
      action: EVENT_ACTIONS.INTERACT,
      event: buildInteractionEventData({
        category: EVENTS_CATEGORY.CONTENT.MANAGE_MODULE.NAME,
        label,
        action: EVENTS_INTERACTIONS.CLICK
      })
    });

    this.setState({ openModule: !this.state.openModule });
    ContentService.lastEditedModule = null;
    updateModulesOpen && updateModulesOpen(id, !this.state.openModule);

    if (!item.loadedPages && item.totalPages) {
      const pages = await ContentService.fetchModulePagesList(
        id,
        isExtra ? isExtra : false
      );

      if (checkAll) {
        await checkAllpages(checkedContent, pages);
      }
    }
  };

  onReceiveDuplicationStatus = () => {
    const { id, isExtra } = this.props;
    const force = true;

    ContentService.fetchModulePagesList(id, isExtra, force);
  };

  openModule = () => {
    this.setState({ openModule: true });
  };

  closeModule = () => {
    this.setState({ openModule: false });
  };

  get openToggle() {
    return (
      <ButtonIcon
        className="accordion__button"
        transparent
        type="secondary"
        icon="chevron-down"
      />
    );
  }

  get closeToggle() {
    return (
      <ButtonIcon
        className="accordion__button"
        transparent
        type="secondary"
        icon="chevron-up"
      />
    );
  }

  isChecked(type, id) {
    const { checkedContent } = this.state;

    if (type === ITEM_TYPE.MODULE) {
      const modulesCheckeds = checkedContent.modules;

      if (modulesCheckeds && modulesCheckeds.length) {
        return modulesCheckeds.findIndex((element) => element.id === id) > -1;
      }

      return false;
    }

    if (type === ITEM_TYPE.PAGE) {
      const pagesCheckeds = checkedContent.pages;

      if (pagesCheckeds && pagesCheckeds.length) {
        return pagesCheckeds.findIndex((element) => element.id === id) > -1;
      }

      return false;
    }

    return false;
  }

  renderChildren = (checkAll, loadedPages, totalPages) => {
    const { id } = this.props;
    const { isPageDragging, isDraggingOver, isModuleDragging, openModule } =
      this.state;
    const opened = !isModuleDragging && openModule;

    const droppableClass = classNames('droppable-page', {
      'droppable-page-dragging': isPageDragging,
      'droppable-page-dragging-over': isDraggingOver,
      'droppable-page-empty': !totalPages,
      'droppable-page-closed': !opened
    });

    return totalPages ? (
      this.renderChildrenWithContent(checkAll, loadedPages, droppableClass)
    ) : (
      <DroppableContent type="page" id={id} className={droppableClass} />
    );
  };

  renderChildrenWithContent = (checkAll, loadedPages, droppableClass) => {
    const { id, isExtra, t } = this.props;
    const { isModuleDragging, openModule } = this.state;
    const opened = !isModuleDragging && openModule;

    if (opened && !loadedPages) {
      ContentService.fetchModulePagesList(id, isExtra ? isExtra : false);
    }

    return (
      <Collapse
        onClick={this.onToggle}
        opened={opened}
        label={t('general.page_plural')}
      >
        {this.renderAccordionBody(loadedPages, checkAll, droppableClass)}
      </Collapse>
    );
  };

  renderAccordionBody(loadedPages, checkAll, droppableClass) {
    const {
      id,
      isMobile,
      dragOnMouseDown,
      checkboxChange,
      isExtra,
      allModulesLoadedPages,
      allModulesExtraLoadedPages
    } = this.props;
    const { nestedChildren, checkedContent, expandModules } = this.state;

    return (
      <>
        {(!expandModules && loadedPages) ||
        (expandModules && isExtra
          ? allModulesExtraLoadedPages
          : allModulesLoadedPages) ? (
          <DroppableContent
            isMobile={isMobile}
            checkboxChange={checkboxChange}
            type="page"
            checkedContent={checkedContent}
            checkAll={checkAll}
            items={nestedChildren}
            id={id}
            className={droppableClass}
            onMouseDown={dragOnMouseDown}
            isExtra={isExtra}
            onReceiveDuplicationStatus={this.onReceiveDuplicationStatus}
          />
        ) : (
          <Loading center />
        )}
      </>
    );
  }
  renderDraggable = () => {
    const {
      type,
      index,
      item,
      id,
      children,
      dragOnMouseDown,
      dragOnMouseUp,
      checkAll,
      isReorderingModulesEnabled
    } = this.props;
    const { isModuleDragging } = this.state;
    const isDragDisabled =
      type === ITEM_TYPE.MODULE && !isReorderingModulesEnabled;
    const isChecked = type === ITEM_TYPE.MODULE && checkAll;
    const dragHandlerClasses = classNames('_mx-sm-3 _mr-4', {
      '_mt-3': type !== ITEM_TYPE.MODULE,
      '_mt-4 _mt-sm-2': type === ITEM_TYPE.MODULE,
      'disabled': isDragDisabled
    });
    const containerClass = classNames(
      `drag-handler-container drag-handler-container-${type}`,
      {
        'drag-handler-container-module-dragging': isModuleDragging,
        'drag-handler-container-module-page': type === ITEM_TYPE.PAGE
      }
    );

    const dragModuleContainer = classNames('drag-module-container', {
      'is-new-module': ContentService.lastCreatedModule === id
    });

    return (
      <Draggable
        key={`${type}-${id}`}
        type={type}
        draggableId={id}
        id={id}
        index={index}
        isDragDisabled={isDragDisabled}
      >
        {(provided, snapshot) => {
          const onMouseUp = (() => {
            const hasDragHandleProps = provided.dragHandleProps;

            if (!hasDragHandleProps) {
              return onMouseUp;
            }

            return (event) => {
              document.removeEventListener('mouseup', onMouseUp);

              dragOnMouseUp && snapshot ? dragOnMouseUp(event) : null;
            };
          })();

          const onMouseDown = (() => {
            const hasDragHandleProps = provided.dragHandleProps;

            if (!hasDragHandleProps) {
              return onMouseDown;
            }

            return (event) => {
              document.addEventListener('mouseup', onMouseUp);

              dragOnMouseDown && snapshot
                ? dragOnMouseDown(
                    event,
                    null,
                    provided.dragHandleProps.onDragStart
                  )
                : provided.dragHandleProps.onDragStart(event);
            };
          })();

          return (
            <Fragment>
              <div
                ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                key={`${type}-${id}`}
                className={dragModuleContainer}
                data-test="draggable-container"
                style={provided.draggableProps.style}
              >
                <div className={containerClass}>
                  <DragHandler
                    {...provided.dragHandleProps}
                    onMouseDown={onMouseDown}
                    className={dragHandlerClasses}
                  />
                  <div className="draggable-content-wrapper">{children}</div>
                </div>
                {type === ITEM_TYPE.MODULE &&
                  this.renderChildren(
                    isChecked,
                    item.loadedPages,
                    item.totalPages
                  )}
              </div>
              {provided.placeholder}
            </Fragment>
          );
        }}
      </Draggable>
    );
  };

  render() {
    const { id, className, onClick } = this.props;
    const { isModuleDragging } = this.state;
    const componentClass = classNames('draggable', className, {
      'draggable-module-dragging': isModuleDragging
    });

    return (
      <div
        id={id}
        ref={this.divElementRef}
        className={componentClass}
        data-test="draggable-module"
        onClick={onClick}
      >
        {this.renderDraggable()}
      </div>
    );
  }
}
